import React from 'react'
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer'

// Define interfaces for invoice data
interface InvoiceItem {
  description: string
  quantity: number
  unit_price: number
  total: number
}

interface CompanyInfo {
  name: string
  address: string
  city: string
  postal_code: string
  country: string
  vat_number?: string
  email: string
  phone?: string
  logo_url?: string
}

interface CustomerInfo {
  name: string
  company_name?: string
  customer_type: 'individual' | 'company'
  street_address: string
  city: string
  postal_code: string
  country: string
  vat_number?: string
  email: string
  phone?: string
}

interface InvoiceData {
  invoice_number: string
  issue_date: string
  due_date: string
  payment_date?: string
  company_info: CompanyInfo
  customer_info: CustomerInfo
  items: InvoiceItem[]
  subtotal: number
  tax_rate: number
  tax_amount: number
  total: number
  currency: string
  payment_method: string
  notes?: string
}

// Create styles
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 40,
    fontSize: 10,
    fontFamily: 'Helvetica',
    lineHeight: 1.4,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 40,
    paddingBottom: 20,
    borderBottomWidth: 2,
    borderBottomColor: '#1e40af',
  },
  logo: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1e40af',
    marginBottom: 10,
  },
  logoImage: {
    width: 150,
    height: 50,
    objectFit: 'contain',
    marginBottom: 10,
  },
  invoiceTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#1e40af',
    textAlign: 'right',
  },
  invoiceNumber: {
    fontSize: 14,
    color: '#64748b',
    textAlign: 'right',
    marginTop: 8,
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#1e40af',
    marginBottom: 10,
    paddingBottom: 5,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  addressSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  addressBox: {
    width: '45%',
  },
  companyName: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 5,
  },
  addressLine: {
    fontSize: 10,
    marginBottom: 2,
    color: '#475569',
  },
  table: {
    marginBottom: 15,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f1f5f9',
    paddingVertical: 8,
    paddingHorizontal: 5,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: '#e2e8f0',
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 6,
    paddingHorizontal: 5,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  tableRowAlt: {
    flexDirection: 'row',
    paddingVertical: 6,
    paddingHorizontal: 5,
    backgroundColor: '#f9fafb',
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  col1: { width: '50%' },
  col2: { width: '15%', textAlign: 'center' },
  col3: { width: '17.5%', textAlign: 'right' },
  col4: { width: '17.5%', textAlign: 'right' },
  tableHeaderText: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#374151',
  },
  tableCellText: {
    fontSize: 10,
    color: '#475569',
  },
  totalsSection: {
    width: '50%',
    alignSelf: 'flex-end',
    marginTop: 10,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 4,
    paddingHorizontal: 10,
  },
  totalRowFinal: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
    paddingHorizontal: 10,
    backgroundColor: '#1e40af',
    color: '#ffffff',
    marginTop: 5,
  },
  totalLabel: {
    fontSize: 11,
    color: '#475569',
  },
  totalValue: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#374151',
  },
  totalFinalLabel: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  totalFinalValue: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  paymentInfo: {
    marginTop: 30,
    padding: 15,
    backgroundColor: '#f8fafc',
    borderRadius: 5,
  },
  paymentTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#374151',
    marginBottom: 8,
  },
  paymentText: {
    fontSize: 10,
    color: '#475569',
    marginBottom: 3,
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    textAlign: 'center',
    fontSize: 8,
    color: '#9ca3af',
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    paddingTop: 10,
  },
  notes: {
    marginTop: 10,
    padding: 12,
    backgroundColor: '#fffbeb',
    borderLeftWidth: 4,
    borderLeftColor: '#f59e0b',
  },
  notesTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#92400e',
    marginBottom: 5,
  },
  notesText: {
    fontSize: 10,
    color: '#78350f',
  },
})

interface InvoiceTemplateProps {
  data: InvoiceData
}

const InvoiceTemplate: React.FC<InvoiceTemplateProps> = ({ data }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: data.currency,
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View>
            {data.company_info.logo_url ? (
              <Image src={data.company_info.logo_url} style={styles.logoImage} />
            ) : (
              <Text style={styles.logo}>{data.company_info.name}</Text>
            )}
            <Text style={styles.addressLine}>Professional Presentation Software</Text>
          </View>
          <View>
            <Text style={styles.invoiceTitle}>INVOICE</Text>
            <Text style={styles.invoiceNumber}>{data.invoice_number}</Text>
          </View>
        </View>

        {/* Company and Customer Info */}
        <View style={styles.addressSection}>
          <View style={styles.addressBox}>
            <Text style={styles.sectionTitle}>From:</Text>
            <Text style={styles.companyName}>{data.company_info.name}</Text>
            <Text style={styles.addressLine}>{data.company_info.address}</Text>
            <Text style={styles.addressLine}>
              {data.company_info.city}, {data.company_info.postal_code}
            </Text>
            <Text style={styles.addressLine}>{data.company_info.country}</Text>
            {data.company_info.vat_number && (
              <Text style={styles.addressLine}>VAT: {data.company_info.vat_number}</Text>
            )}
            <Text style={styles.addressLine}>{data.company_info.email}</Text>
            {data.company_info.phone && (
              <Text style={styles.addressLine}>{data.company_info.phone}</Text>
            )}
          </View>

          <View style={styles.addressBox}>
            <Text style={styles.sectionTitle}>Bill To:</Text>
            {data.customer_info.customer_type === 'company' && data.customer_info.company_name ? (
              <Text style={styles.companyName}>{data.customer_info.company_name}</Text>
            ) : (
              <Text style={styles.companyName}>{data.customer_info.name}</Text>
            )}
            {data.customer_info.customer_type === 'company' && data.customer_info.company_name && (
              <Text style={styles.addressLine}>Attn: {data.customer_info.name}</Text>
            )}
            <Text style={styles.addressLine}>{data.customer_info.street_address}</Text>
            <Text style={styles.addressLine}>
              {data.customer_info.city}, {data.customer_info.postal_code}
            </Text>
            <Text style={styles.addressLine}>{data.customer_info.country}</Text>
            {data.customer_info.vat_number && (
              <Text style={styles.addressLine}>VAT: {data.customer_info.vat_number}</Text>
            )}
            <Text style={styles.addressLine}>{data.customer_info.email}</Text>
          </View>
        </View>

        {/* Invoice Details */}
        <View style={styles.section}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <View>
              <Text style={styles.totalLabel}>Issue Date:</Text>
              <Text style={styles.totalValue}>{formatDate(data.issue_date)}</Text>
            </View>
            <View>
              <Text style={styles.totalLabel}>Due Date:</Text>
              <Text style={styles.totalValue}>{formatDate(data.due_date)}</Text>
            </View>
            {data.payment_date && (
              <View>
                <Text style={styles.totalLabel}>Payment Date:</Text>
                <Text style={styles.totalValue}>{formatDate(data.payment_date)}</Text>
              </View>
            )}
          </View>
        </View>

        {/* Items Table */}
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderText, styles.col1]}>Description</Text>
            <Text style={[styles.tableHeaderText, styles.col2]}>Qty</Text>
            <Text style={[styles.tableHeaderText, styles.col3]}>Unit Price</Text>
            <Text style={[styles.tableHeaderText, styles.col4]}>Total</Text>
          </View>
          
          {data.items.map((item, index) => (
            <View key={index} style={index % 2 === 0 ? styles.tableRow : styles.tableRowAlt}>
              <Text style={[styles.tableCellText, styles.col1]}>{item.description}</Text>
              <Text style={[styles.tableCellText, styles.col2]}>{item.quantity}</Text>
              <Text style={[styles.tableCellText, styles.col3]}>{formatCurrency(item.unit_price)}</Text>
              <Text style={[styles.tableCellText, styles.col4]}>{formatCurrency(item.total)}</Text>
            </View>
          ))}
        </View>

        {/* Totals */}
        <View style={styles.totalsSection}>
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Subtotal:</Text>
            <Text style={styles.totalValue}>{formatCurrency(data.subtotal)}</Text>
          </View>
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Tax ({(data.tax_rate * 100).toFixed(0)}%):</Text>
            <Text style={styles.totalValue}>{formatCurrency(data.tax_amount)}</Text>
          </View>
          <View style={styles.totalRowFinal}>
            <Text style={styles.totalFinalLabel}>Total Amount:</Text>
            <Text style={styles.totalFinalValue}>{formatCurrency(data.total)}</Text>
          </View>
        </View>

        {/* Payment Information */}
        <View style={styles.paymentInfo}>
          <Text style={styles.paymentTitle}>Payment Information</Text>
          <Text style={styles.paymentText}>Payment Method: {data.payment_method}</Text>
          <Text style={styles.paymentText}>
            Payment was processed on {data.payment_date ? formatDate(data.payment_date) : 'N/A'}
          </Text>
          <Text style={styles.paymentText}>
            Thank you for choosing PresentClic for your presentation needs.
          </Text>
        </View>

        {/* Notes */}
        {data.notes && (
          <View style={styles.notes}>
            <Text style={styles.notesTitle}>Notes:</Text>
            <Text style={styles.notesText}>{data.notes}</Text>
          </View>
        )}

        {/* Footer */}
        <Text style={styles.footer}>
          This invoice was automatically generated by PresentClic billing system. 
          For support, please contact us at support@presentclic.com
        </Text>
      </Page>
    </Document>
  )
}

export default InvoiceTemplate